/* ============================================================
   InlineTextEditor — Click-to-edit text in CMS preview
   ─────────────────────────────────────────────────────────
   Supports heading, body, and caption variants.
   Flat IDE aesthetic with minimal chrome.
   ============================================================ */

import { useRef, useState, useCallback } from 'react'

interface InlineTextEditorProps {
  value: string
  onChange: (newValue: string) => void
  variant?: 'heading' | 'body' | 'caption'
  placeholder?: string
  multiline?: boolean
  className?: string
  disabled?: boolean
}

const VARIANT_STYLES: Record<string, React.CSSProperties> = {
  heading: { fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' },
  body:    { fontSize: 14, lineHeight: 1.7 },
  caption: { fontSize: 12, color: 'var(--editor-text-muted, rgba(255,255,255,0.6))' },
}

export default function InlineTextEditor({
  value,
  onChange,
  variant = 'body',
  placeholder = 'Click to edit...',
  multiline = false,
  className = '',
  disabled = false,
}: InlineTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isEditing, setIsEditing] = useState(false)

  const startEditing = useCallback(() => {
    if (disabled) return
    setIsEditing(true)
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus()
        const range = document.createRange()
        range.selectNodeContents(ref.current)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    })
  }, [disabled])

  const save = useCallback(() => {
    const newValue = ref.current?.textContent?.trim() || ''
    setIsEditing(false)
    if (newValue !== value) onChange(newValue)
  }, [onChange, value])

  const cancel = useCallback(() => {
    setIsEditing(false)
    if (ref.current) ref.current.textContent = value
  }, [value])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel() }
      if (e.key === 'Enter' && !multiline) { e.preventDefault(); save() }
    },
    [multiline, save, cancel],
  )

  const handleBlur = useCallback(() => {
    if (isEditing) save()
  }, [isEditing, save])

  const baseStyle: React.CSSProperties = {
    ...VARIANT_STYLES[variant] || VARIANT_STYLES.body,
    borderRadius: 'var(--editor-radius, 4px)',
    padding: '1px 4px',
    margin: '0 -4px',
    transition: 'background 0.15s ease, outline 0.15s ease',
    cursor: disabled ? 'default' : isEditing ? 'text' : 'text',
    outline: isEditing ? '2px solid var(--editor-accent, #3b82f6)' : 'none',
    outlineOffset: 1,
    background: isEditing
      ? 'rgba(255,255,255,0.04)'
      : 'transparent',
    fontStyle: !value && !isEditing ? 'italic' : undefined,
    color: !value && !isEditing ? 'var(--editor-text-dim, rgba(255,255,255,0.3))' : undefined,
  }

  return (
    <div
      ref={ref}
      role="textbox"
      tabIndex={disabled ? -1 : 0}
      contentEditable={isEditing}
      suppressContentEditableWarning
      className={className}
      style={baseStyle}
      onClick={!isEditing ? startEditing : undefined}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      aria-label={`Edit ${variant} text`}
      data-placeholder={placeholder}
    >
      {isEditing ? value : value || placeholder}
    </div>
  )
}
